import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarDays, MapPin, Ticket, Loader2, CheckCircle2 } from "lucide-react";

export default function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // ticket for QR modal

  useEffect(() => {
    api.get("/tickets/mine").then((r) => setTickets(r.data.tickets || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="container-mc py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;

  return (
    <div className="container-mc py-14" data-testid="my-tickets-page">
      <p className="overline mb-2">🎟️ Mes billets</p>
      <h1 className="font-display font-black text-4xl tracking-tighter">Vos billets</h1>
      <p className="text-sm text-muted-foreground mt-2 mb-8">Présentez le QR code à l'entrée de l'événement. Il est unique et à usage unique.</p>

      {tickets.length === 0 ? (
        <div className="card-mc p-16 text-center">
          <Ticket className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">Vous n'avez pas encore acheté de billet.</p>
          <Link to="/events" className="btn-primary inline-flex mt-6" data-testid="browse-events-btn">Voir les événements</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {tickets.map((t) => {
            const dateStr = t.event?.event_date
              ? new Date(t.event.event_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
              : "";
            return (
              <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => setOpen(t)}
                      className="card-mc p-5 text-left hover:border-foreground transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="overline mb-1">{t.event?.artist_name}</p>
                    <h3 className="font-display font-black text-xl tracking-tighter">{t.event?.title}</h3>
                  </div>
                  {t.status === "used" ? (
                    <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-muted text-muted-foreground inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Utilisé
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-[hsl(var(--accent))] text-white">Valide</span>
                  )}
                </div>
                <div className="mt-3 flex flex-col gap-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> {dateStr}</span>
                  <span className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {t.event?.venue} · {t.event?.city}</span>
                </div>
                <div className="mt-3 flex justify-between items-center border-t border-border pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-wider">{t.ticket_type_name}{t.code ? ` · ${t.code}` : ""}</p>
                  <p className="text-xs font-bold underline">Voir le QR code →</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="qr-modal" onClick={() => setOpen(null)}>
          <div className="bg-card w-full max-w-sm card-mc p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="gn-strip h-1.5 -mt-8 -mx-8 mb-6" />
            <p className="overline mb-1">{open.event?.artist_name}</p>
            <h3 className="font-display font-black text-2xl tracking-tighter">{open.event?.title}</h3>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-2">{open.ticket_type_name}</p>
            <div className="mt-6 p-4 bg-white border-2 border-foreground inline-block">
              <img src={open.qr} alt="QR code" className="w-56 h-56" />
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Code billet : <span className="font-mono font-bold text-foreground" data-testid="ticket-code">{open.code || open.id.slice(0, 8).toUpperCase()}</span></p>
            <p className="mt-1 text-[10px] text-muted-foreground">Code unique infalsifiable · utilisable en secours si le QR ne se scanne pas</p>
            {open.status === "used" && (
              <p className="mt-3 text-xs font-bold text-destructive">⚠ Billet déjà utilisé</p>
            )}
            <button data-testid="close-qr-btn" onClick={() => setOpen(null)} className="btn-ghost mt-6 w-full">Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
